import { useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { clearAllTournamentRoles } from '@/utils/tournamentRoleCache';
import { pendingInvite } from '@/utils/pendingInvite';
import { useTelegram } from '@/hooks/useTelegram';
import type { User } from '@/types';
import { AuthContext, type RegisterData } from '@/hooks/useAuth';

interface AuthProviderProps {
  children: ReactNode;
}

// Centralized extraction of `{ tokens, user }` from auth endpoints.
// Backend shape varies: `/auth/telegram` and `/auth/login` return `{ data: { tokens, user } }` (nested),
// while older endpoints sometimes return `{ tokens, user }` at the top level. This helper handles both.
// `apiClient.setTokens` already tolerates `accessToken` / `access_token` / `token`, so callers don't
// need to normalize the inner token keys — just pass the `tokens` object through.
// Do NOT use for endpoints that return `{ token }` (singular, e.g. `/auth/link-telegram`,
// `/auth/register-telegram-email`) — those need their own flat extraction.
function extractAuthPayload(response: any): { tokens: any; user: any } {
  const data = response?.data?.data || response?.data || response;
  return {
    tokens: data?.tokens || data,
    user: data?.user || response?.user,
  };
}

// Read cached user synchronously for optimistic first render (avoids auth FOUC in Header)
function getCachedUser(): User | null {
  try {
    const tokens = apiClient.getTokens();
    if (!tokens.access_token) return null;

    const savedUser = localStorage.getItem('currentUser');
    if (!savedUser || savedUser === 'undefined') return null;

    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) return null;
    const lastActivityTime = Number.parseInt(lastActivity, 10);
    if (!Number.isFinite(lastActivityTime)) return null;
    const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    if (lastActivityTime < weekAgo) return null;

    return JSON.parse(savedUser);
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(getCachedUser);
  const [isLoading, setIsLoading] = useState(true);
  const [telegramUserData, setTelegramUserData] = useState<any>(null);
  const [pendingTelegramWelcome, setPendingTelegramWelcome] = useState(false);
  const { initData, isLoaded: telegramLoaded, isInTelegram } = useTelegram();
  const queryClient = useQueryClient();

  const isAuthenticated = !!user;
  const isGuest = !isAuthenticated;
  const isTelegramAvailable = telegramLoaded && (!!initData || process.env.NODE_ENV === 'development');

  useEffect(() => {
    if (!telegramLoaded) return;

    const initializeAuth = async () => {
      try {
        // Check for existing token and user
        const tokens = apiClient.getTokens();
        const existingToken = tokens.access_token;
        const savedUser = localStorage.getItem('currentUser');
        const lastActivity = localStorage.getItem('lastActivity');
        
        if (existingToken && savedUser && savedUser !== 'undefined' && lastActivity) {
          try {
            const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000); // 1 week in milliseconds
            const lastActivityTime = Number.parseInt(lastActivity, 10);

            // Treat non-numeric lastActivity as corrupt state
            if (!Number.isFinite(lastActivityTime)) {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('currentUser');
              localStorage.removeItem('lastActivity');
              setUser(null);
              setIsLoading(false);
              return;
            }

            // Check if user has been inactive for more than a week
            if (lastActivityTime < weekAgo) {
              // Session expired — drop both server-side and React-side auth state so the UI
              // doesn't briefly render an authenticated shell before the redirect kicks in
              apiClient.logout();
              localStorage.removeItem('currentUser');
              localStorage.removeItem('lastActivity');
              queryClient.clear();
              setUser(null);
              setIsLoading(false);
              return;
            }

            // Verify token and check user status on server
            try {
              const verifyResponse = await apiClient.verifyToken();
              const verifiedUser = verifyResponse.data.data.user;

              // Update last activity and restore session with verified user data
              localStorage.setItem('lastActivity', Date.now().toString());
              localStorage.setItem('currentUser', JSON.stringify(verifiedUser));
              setUser(verifiedUser);
              setIsLoading(false);
              return;
            } catch (verifyError: any) {
              // If token verification fails (e.g., user blocked, token invalid)
              console.error('Token verification failed:', verifyError);

              // Clear session data
              apiClient.logout();
              localStorage.removeItem('currentUser');
              localStorage.removeItem('lastActivity');

              // Check if account is blocked
              if (verifyError.response?.status === 403 &&
                  verifyError.response?.data?.error === 'account_blocked') {
                // Blocked account info is already stored by axios interceptor
                // Drop the cached user so authenticated chrome doesn't flash before redirect
                queryClient.clear();
                setUser(null);
                setIsLoading(false);
                return;
              }

              // For other errors, clear React state too so Header/Sidebar/BottomNav don't
              // keep rendering the authenticated shell over a now-empty ProtectedRoute
              queryClient.clear();
              setUser(null);
              setIsLoading(false);
              return;
            }
          } catch (error) {
            console.error('Failed to parse savedUser from localStorage:', error);
            // Clear corrupted data
            apiClient.logout();
            localStorage.removeItem('currentUser');
            localStorage.removeItem('lastActivity');
            queryClient.clear();
            setUser(null);
          }
        } else {
          // Partial/corrupt auth state: some auth data exists but not all three
          // required pieces (token + currentUser + lastActivity).
          // Clear everything to prevent stale tokens from being sent via axios.
          const hasAnyAuthData = existingToken || savedUser || lastActivity;
          if (hasAnyAuthData) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('currentUser');
            localStorage.removeItem('lastActivity');
            setUser(null);
          }

          // Silent Telegram re-auth on cold start:
          // when there's no cached session but the mini app was launched inside Telegram
          // and we have initData, log in transparently so users don't see LandingPage's
          // "press the button" CTA after their JWT expires.
          if (isInTelegram && initData) {
            try {
              const response = await apiClient.loginWithTelegram(initData);
              const { tokens, user: userData } = extractAuthPayload(response);

              if (userData && (tokens?.accessToken || tokens?.access_token || tokens?.token)) {
                apiClient.setTokens(tokens);
                setUser(userData);
                localStorage.setItem('currentUser', JSON.stringify(userData));
                localStorage.setItem('lastActivity', Date.now().toString());
              }
            } catch (silentTgError) {
              // Expected outcomes: 404 (user not registered → LandingPage handles flow),
              // network error, or invalid initData. Fall through to anonymous state.
              console.warn('Silent Telegram re-auth skipped:', silentTgError);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        // Clear potentially corrupted data
        apiClient.logout();
        localStorage.removeItem('currentUser');
        localStorage.removeItem('lastActivity');
        queryClient.clear();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // queryClient is stable across renders, isInTelegram is derived from telegramLoaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramLoaded, initData, isInTelegram]);

  // Update activity on any user interaction
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Update activity on various user interactions
    const events = ['click', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [isAuthenticated]);

  // Periodic status check for logged-in users
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkUserStatus = async () => {
      try {
        await apiClient.verifyToken();
        // If successful, user is still active - do nothing
      } catch {
        // If blocked or token invalid, axios interceptor will handle redirect
      }
    };

    // Check status every 30 seconds
    const intervalId = setInterval(checkUserStatus, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const login = async (telegramInitData: string) => {
    try {
      setIsLoading(true);
      const response = await apiClient.loginWithTelegram(telegramInitData);

      const { tokens, user: userData } = extractAuthPayload(response);

      apiClient.setTokens(tokens);
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Telegram login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await apiClient.loginWithEmail(email, password);

      const { tokens, user: userData } = extractAuthPayload(response);

      if (!tokens?.access_token && !tokens?.accessToken && !tokens?.token) {
        throw new Error('No authentication token received from server');
      }

      if (!userData) {
        throw new Error('No user data received from server');
      }

      apiClient.setTokens(tokens);
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Email login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setIsLoading(true);
      const response = await apiClient.register(data);

      const { tokens, user: userData } = extractAuthPayload(response);

      apiClient.setTokens(tokens);
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTelegram = async () => {
    if (!initData && process.env.NODE_ENV !== 'development') {
      throw new Error('Telegram data is not available');
    }

    try {
      setIsLoading(true);
      // Use dev mode for browser testing
      await login(initData || 'dev_mode=true');
    } catch (error: any) {
      console.error('Telegram login failed:', error);

      // Check if this is a 404 error with telegram_data (new user)
      if (error.response?.status === 404 && error.response?.data?.telegram_data) {
        // New Telegram user - redirect to welcome page
        const telegramData = error.response.data.telegram_data;
        setTelegramUserData(telegramData);
        setPendingTelegramWelcome(true);
        setIsLoading(false);
        return;
      }

      // For other errors, throw them
      setIsLoading(false);
      throw error;
    }
  };

  const linkTelegramAccount = async (email: string, password: string) => {
    if (!initData) {
      throw new Error('Telegram data is not available');
    }

    try {
      setIsLoading(true);
      const response = await apiClient.linkTelegramWithEmail({
        initData,
        email,
        password,
      });

      // `/auth/link-telegram` returns a FLAT shape `{ success, token, user }` (singular `token`),
      // NOT the nested `{ data: { tokens, user } }`. Don't use `extractAuthPayload` here.
      const userData = response.data?.user;
      const token = response.data?.token;

      apiClient.setTokens({ accessToken: token });
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Telegram linking failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleTelegramEmailSubmit = async (email: string, password: string, telegramData: any, mode: 'register' | 'link' = 'register') => {
    try {
      setIsLoading(true);

      let response;
      if (mode === 'link') {
        response = await apiClient.linkTelegram(email, password, telegramData);
      } else {
        response = await apiClient.registerTelegramEmail(email, password, telegramData);
      }

      if (response.data.success) {
        const userData = response.data.user;
        const token = response.data.token;

        apiClient.setTokens({ accessToken: token });
        setUser(userData);
        localStorage.setItem('currentUser', JSON.stringify(userData));
        localStorage.setItem('lastActivity', Date.now().toString());

        setPendingTelegramWelcome(false);
        setTelegramUserData(null);
      }
    } catch (error) {
      console.error('Failed to handle Telegram email submit:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      throw new Error('Google OAuth интеграция находится в разработке. Будет доступна в следующих версиях.');
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setIsLoading(true);
      throw new Error('Apple OAuth интеграция находится в разработке. Будет доступна в следующих версиях.');
    } catch (error) {
      console.error('Apple login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const registerWithTelegram = async () => {
    if (!initData) {
      throw new Error('Telegram data is not available');
    }

    try {
      setIsLoading(true);
      const response = await apiClient.registerWithTelegram(initData);

      // `/auth/telegram` returns `{ data: { tokens, user } }` (nested) — same shape as login.
      // Previously this code passed `response.data.data` (or `response.data`) directly to
      // `setTokens`, which is the `{ tokens, user }` wrapper, NOT the tokens object — so
      // `setTokens` couldn't find `access_token` and the user was registered but anonymous.
      const { tokens, user: userData } = extractAuthPayload(response);

      apiClient.setTokens(tokens);
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Telegram registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const registerWithGoogle = async () => {
    try {
      setIsLoading(true);
      throw new Error('Google OAuth интеграция находится в разработке. Будет доступна в следующих версиях.');
    } catch (error) {
      console.error('Google registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const registerWithApple = async () => {
    try {
      setIsLoading(true);
      throw new Error('Apple OAuth интеграция находится в разработке. Будет доступна в следующих версиях.');
    } catch (error) {
      console.error('Apple registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const registerTelegramOnly = async (telegramData: any) => {
    try {
      setIsLoading(true);
      const response = await apiClient.registerTelegramOnly(telegramData);

      const data = response.data.data || response.data;
      const userData = data.user;
      const token = data.token;

      apiClient.setTokens({ accessToken: token });
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('lastActivity', Date.now().toString());

      setPendingTelegramWelcome(false);
    } catch (error) {
      console.error('Telegram-only registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const bindEmail = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await apiClient.bindEmail(email, password);

      const data = response.data.data || response.data;
      const updatedUser = data.user;

      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Email binding failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearPendingTelegramWelcome = () => {
    setPendingTelegramWelcome(false);
  };

  const deleteAccount = async (password: string) => {
    try {
      setIsLoading(true);
      await apiClient.deleteAccount(password);

      // Clear all local auth data synchronously
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('lastActivity');
      clearAllTournamentRoles();
      queryClient.clear();
      setUser(null);


    } catch (error) {
      console.error('Delete account failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Capture token before clearing so server logout can still authenticate
    const token = localStorage.getItem('accessToken');
    // Clear all local auth data synchronously
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('lastActivity');
    clearAllTournamentRoles();
    pendingInvite.clear();
    // Clear React Query cache to prevent cross-user data contamination
    queryClient.clear();
    // Clear React auth state — batched with caller's navigate() in React 18
    setUser(null);
    // Fire-and-forget server logout with captured token
    if (token) {
      apiClient.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }

  };

  const value = {
    user,
    setUser,
    isAuthenticated,
    isLoading,
    telegramUserData,
    pendingTelegramWelcome,
    login,
    loginWithEmail,
    loginWithTelegram,
    loginWithGoogle,
    loginWithApple,
    linkTelegramAccount,
    handleTelegramEmailSubmit,
    registerTelegramOnly,
    bindEmail,
    register,
    registerWithTelegram,
    registerWithGoogle,
    registerWithApple,
    deleteAccount,
    clearPendingTelegramWelcome,
    logout,
    isGuest,
    isTelegramAvailable,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
